import { Box, capitalize, MenuItem, TextField } from '@mui/material';
import { PrintWorkoutLayout } from '@strength/domain';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { MultiSelect } from '../../../components/input/multi-select';
import { BlockPreview } from '../types';

export const WorkoutSelector = ({
  block,
  layout,
  cycleLabel,
  cycleIds,
  setCycleIds,
  sessionLabel,
  sessionIds,
  setSessionIds,
  setExerciseId,
  setSets,
}: {
  layout: PrintWorkoutLayout;
  block: BlockPreview;
  cycleLabel: string;
  cycleIds: number[];
  setCycleIds: (ids: number[]) => void;
  sessionLabel: string;
  sessionIds: number[];
  setSessionIds: (ids: number[]) => void;
  setExerciseId: (id: string | null) => void;
  setSets: (sets: number[] | null) => void;
}) => {
  const { t } = useTranslation();
  const useSingleWorkoutLayout = layout === 'workout' || layout === 'exercise';

  const singleCycleSelect = (
    <WorkoutOptionWrapper>
      <TextField
        select
        label={capitalize(cycleLabel)}
        value={cycleIds[0] ?? ''}
        onChange={(e) => {
          const newCycleId = Number(e.target.value);
          setCycleIds([newCycleId]);
          // Session count differs per cycle; reset to the first day so the
          // selected session stays valid in the newly selected cycle.
          setSessionIds([1]);
          setExerciseId(null);
          setSets(null);
        }}
      >
        {block.sortedCycles.map((c) => (
          <MenuItem key={c.id} value={c.id}>
            {c.name}
          </MenuItem>
        ))}
      </TextField>
    </WorkoutOptionWrapper>
  );

  // Session count for the currently selected cycle. BlockLayout maps each
  // cycle's sortOrder to its session count, so cycles can differ (e.g. week 1
  // has 4 days, week 2 has 7). The flat `block.sortedSessions` list only ever
  // reflects the first cycle, so derive options from the selected cycle here.
  const selectedCycle = cycleIds[0];
  const selectedCycleSessionCount = selectedCycle
    ? block.layout[selectedCycle] ?? 0
    : 0;
  const singleSessionOptions = Array.from(
    { length: selectedCycleSessionCount },
    (_, index) => ({
      id: index + 1,
      name: `${sessionLabel} ${index + 1}`,
    }),
  );

  const singleSessionSelect = (
    <WorkoutOptionWrapper>
      <TextField
        select
        label={capitalize(sessionLabel)}
        value={
          singleSessionOptions.some((s) => s.id === sessionIds[0])
            ? sessionIds[0]
            : ''
        }
        onChange={(e) => {
          setSessionIds([Number(e.target.value)]);
          setExerciseId(null);
          setSets(null);
        }}
      >
        {singleSessionOptions.map((s) => (
          <MenuItem key={s.id} value={s.id}>
            {s.name}
          </MenuItem>
        ))}
      </TextField>
    </WorkoutOptionWrapper>
  );

  // For the multi-select path the session list is scoped to the selected
  // cycles, not the flat first-cycle sortedSessions list. A single cycle shows
  // every day it has; multiple cycles (or none) show only the days common to
  // all selected cycles — i.e. the minimum session count — since a day that
  // doesn't exist in one cycle can't be printed for it.
  const multiSessionCount = useMemo(() => {
    const cycles = cycleIds.length
      ? cycleIds
      : block.sortedCycles.map((c) => c.id);
    if (!cycles.length) return 0;
    if (cycles.length === 1) {
      const cycle = cycles[0];
      return cycle ? block.layout[cycle] ?? 0 : 0;
    }
    return Math.min(...cycles.map((id) => block.layout[id] ?? 0));
  }, [cycleIds, block.sortedCycles, block.layout]);
  const multiSessionOptions = Array.from(
    { length: multiSessionCount },
    (_, index) => ({
      id: String(index + 1),
      name: `${sessionLabel} ${index + 1}`,
    }),
  );

  const multiCycleSelect = (
    <WorkoutOptionWrapper>
      <MultiSelect
        label={capitalize(cycleLabel)}
        options={block.sortedCycles.map((c) => ({
          id: String(c.id),
          name: c.name,
        }))}
        selectedOptions={block.sortedCycles
          .filter((c) => cycleIds.includes(c.id))
          .map((c) => ({
            id: String(c.id),
            name: c.name,
          }))}
        onChange={(e) => {
          const nextCycleIds = e.map((c) => Number(c.id));
          setCycleIds(nextCycleIds);
          // Adding a shorter cycle shrinks the available day range; drop any
          // selected days that no longer exist in the new cycle set.
          const nextCycles = nextCycleIds.length
            ? nextCycleIds
            : block.sortedCycles.map((c) => c.id);
          const nextCycle = nextCycles[0];
          const nextMax =
            nextCycles.length === 1
              ? nextCycle
                ? block.layout[nextCycle] ?? 0
                : 0
              : Math.min(...nextCycles.map((id) => block.layout[id] ?? 0));
          setSessionIds(sessionIds.filter((id) => id <= nextMax));
          setExerciseId(null);
          setSets(null);
        }}
        optionToName={(c) => c.name}
        placeholder={t('all')}
        showSelectAll={true}
      />
    </WorkoutOptionWrapper>
  );

  const multiSessionSelect = (
    <WorkoutOptionWrapper>
      <MultiSelect
        label={capitalize(sessionLabel)}
        options={multiSessionOptions}
        selectedOptions={multiSessionOptions.filter((s) =>
          sessionIds.includes(Number(s.id)),
        )}
        onChange={(e) => {
          setSessionIds(e.map((s) => Number(s.id)));
          setExerciseId(null);
          setSets(null);
        }}
        optionToName={(s) => s.name}
        placeholder={t('all')}
        showSelectAll={true}
      />
    </WorkoutOptionWrapper>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2 }}>
      {useSingleWorkoutLayout && (
        <>
          {singleCycleSelect}
          {singleSessionSelect}
        </>
      )}
      {(layout === 'cycle' ||
        layout === 'session' ||
        layout === 'uniform-session') && (
        <>
          {multiCycleSelect}
          {multiSessionSelect}
        </>
      )}
    </Box>
  );
};

const WorkoutOptionWrapper = ({ children }: { children: React.ReactNode }) => {
  return (
    <Box
      sx={{ display: 'flex', flexDirection: 'column', width: '50%', gap: 0.5 }}
    >
      {children}
    </Box>
  );
};
